import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@contexts/app';
import { useJobRefetch } from '@hooks/job/refetch';
import { queryKeys } from '@utils/consts';
import { CROSS_TAB_CHANNEL_NAME } from './constants';
import { CrossTabMessage } from './types';

const channel =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CROSS_TAB_CHANNEL_NAME);

export const broadcastCrossTabMessage = (message: CrossTabMessage) => {
  channel?.postMessage(message);
};

export const useCrossTabSync = () => {
  const { id } = useAppContext();
  const queryClient = useQueryClient();
  const { refetch } = useJobRefetch({ schedule: { type: 'internal' } });

  const handlers = useRef({ id, refetch });
  // eslint-disable-next-line immutable/no-mutation
  handlers.current = { id, refetch };

  useEffect(() => {
    if (!channel) return;

    const onMessage = async ({ data }: MessageEvent<CrossTabMessage>) => {
      const { id: userId, refetch: refetchJobs } = handlers.current;
      if (!userId || data?.userId !== userId) return;

      if (data.type === 'displayOptions') {
        await refetchJobs();
        await queryClient.invalidateQueries({ queryKey: [...queryKeys.user, userId] });
      }
    };

    channel.addEventListener('message', onMessage);
    return () => channel.removeEventListener('message', onMessage);
  }, [queryClient]);
};
